import { type SxProps, type Theme } from '@mui/material/styles';

export const formStyles: Record<string, SxProps<Theme>> = {
  formContainer: {
    p: 0,
    backgroundColor: 'transparent',
    fontSize: '13px',
    boxShadow: 'none',
    border: 'none',
    borderRadius: 0,
    elevation: 0,
  },

  formTitle: {
    mb: 2,
    fontWeight: 500,
    fontSize: '16px',
    color: 'text.primary',
  },

  // Main grid container
  formGrid: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(20, 1fr)', // 20 columns for 5% granularity
    gridAutoRows: 'minmax(40px, auto)',
    gap: 0,
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: 1,
    overflow: 'hidden',
    backgroundColor: '#fafafa',

    '& > *': {
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderColor: theme.palette.divider,
    },

    // Stack all boxes vertically on mobile
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr', // Single column on mobile
      '& > *': {
        gridColumn: 'span 1 !important', // Override all span values
        borderRight: 'none',
        borderBottom: '1px solid',
        borderColor: theme.palette.divider,
      },
    },
  }),

  // Base box styles
  box: {
    p: 1.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    overflow: 'hidden', // Prevent content overflow
    backgroundColor: '#ffffff',
    transition: 'all 0.3s ease',
    scrollMarginTop: '110px', // Offset for sticky header when scrolling into view

    '&:hover': {
      backgroundColor: '#fafafa',
    },

    // Highlight animation for field navigation
    '&.highlighted-box': {
      outline: '3px solid #1976d2',
      outlineOffset: '-3px',
      backgroundColor: '#e3f2fd !important',
      animation: 'highlight-pulse 2s ease-in-out forwards',
      zIndex: 1,
    },

    '@keyframes highlight-pulse': {
      '0%': {
        outlineOffset: '8px',
        outlineWidth: '0px',
        opacity: 0,
      },
      '10%': {
        outlineOffset: '-3px',
        outlineWidth: '3px',
        opacity: 1,
      },
      '30%, 70%': {
        outlineOffset: '-3px',
        outlineWidth: '3px',
      },
      '50%': {
        outlineOffset: '-5px',
        outlineWidth: '4px',
      },
      '90%': {
        opacity: 1,
      },
      '100%': {
        opacity: 0,
        outlineWidth: '0px',
      },
    },

    '& .MuiTypography-caption': {
      fontSize: '11px',
      fontWeight: 500,
      mb: 0.5,
      color: 'text.secondary',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },

    '& .MuiTextField-root': {
      width: '100%', // Ensure inputs don't exceed box width
      '& .MuiInputBase-input': {
        padding: '4px 8px',
        fontSize: '13px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.4)',
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: 2,
      },
      // Error state styling - make it more prominent
      '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: 'error.main',
        borderWidth: 2,
      },
      '& .MuiFormHelperText-root.Mui-error': {
        color: 'error.main',
        fontSize: '11px',
        marginTop: '3px',
      },
    },

    '& .MuiCheckbox-root': {
      padding: '4px',
      color: 'text.secondary',
    },

    '& .MuiRadio-root': {
      padding: '4px',
      // Let MUI handle the color (gray unchecked, primary checked)
    },

    '& .MuiFormControlLabel-root': {
      ml: 0,
      mr: 1.5,
      '& .MuiFormControlLabel-label': {
        fontSize: '12px',
        color: 'text.primary',
      },
    },
  },

  // Grid-based box positioning
  // Row 1: Boxes 1 & 1a
  box1: {
    gridColumn: 'span 12', // 60%
  },

  box1a: {
    gridColumn: 'span 8', // 40%
  },

  // Row 2: Boxes 2, 3, 4
  box2: {
    gridColumn: 'span 8', // 40%
  },

  box3: {
    gridColumn: 'span 4', // 20%
    flexDirection: 'row',
    flexWrap: 'wrap',
  },

  box4: {
    gridColumn: 'span 8', // 40%
  },

  // Row 3: Boxes 5, 6/8, 7
  box5: {
    gridColumn: 'span 8', // 40%
  },

  box6and8: {
    gridColumn: 'span 4', // 20%
    gridRow: 'span 1', // Will contain nested grid for 6 and 8
    display: 'grid',
    gridTemplateRows: '1fr auto',
    gap: 0,
    p: 0,
  },

  box6: {
    borderBottom: '1px solid',
    borderColor: 'divider',
    p: 1.5,
    backgroundColor: '#ffffff',
  },

  box8: {
    p: 1.5,
    backgroundColor: '#ffffff',
  },

  box7: {
    gridColumn: 'span 8', // 40%
  },

  // Row 4: Boxes 9, 10, 11
  box9: {
    gridColumn: 'span 8', // 40%
  },

  box10: {
    gridColumn: 'span 4', // 20%
  },

  box11: {
    gridColumn: 'span 8', // 40%
  },

  // Row 5: Boxes 12, 13
  box12: {
    gridColumn: 'span 12', // 60%
  },

  box13: {
    gridColumn: 'span 8', // 40%
  },

  // Row 6: Boxes 14, 15, 16
  box14: {
    gridColumn: 'span 6', // 30%
  },

  box15: {
    gridColumn: 'span 6', // 30%
  },

  box16: {
    gridColumn: 'span 8', // 40%
  },

  // Row 7: Boxes 17, 18
  box17: {
    gridColumn: 'span 12', // 60%
  },

  box18: {
    gridColumn: 'span 8', // 40%
  },

  // Row 8: Boxes 19, 20
  box19: {
    gridColumn: 'span 12', // 60%
  },

  box20: {
    gridColumn: 'span 8', // 40%
  },

  // Row 9: Boxes 21, 22/23
  box21: {
    gridColumn: 'span 12', // 60%
  },

  box22and23: {
    gridColumn: 'span 8', // 40%
    display: 'grid',
    gridTemplateRows: '1fr 1fr',
    gap: 0,
    p: 0,
  },

  box22: {
    borderBottom: '1px solid',
    borderColor: 'divider',
    p: 1.5,
    backgroundColor: '#ffffff',
  },

  box23: {
    p: 1.5,
    backgroundColor: '#ffffff',
  },

  // Row 10: Box 24 (Service Lines) - spans full width
  box24: {
    gridColumn: 'span 20', // 100%
    overflowX: 'auto', // Allow horizontal scrolling for the service lines table
    overflowY: 'hidden',
  },

  // Row 11: Boxes 25-30
  box25: {
    gridColumn: 'span 4', // 20%
  },

  box26: {
    gridColumn: 'span 4', // 20%
  },

  box27: {
    gridColumn: 'span 4', // 20%
  },

  box28: {
    gridColumn: 'span 3', // 15%
  },

  box29: {
    gridColumn: 'span 3', // 15%
  },

  box30: {
    gridColumn: 'span 2', // 10%
  },

  // Row 12: Boxes 31, 32, 33
  box31: {
    gridColumn: 'span 4', // 20%
  },

  box32: {
    gridColumn: 'span 8', // 40%
  },

  box33: {
    gridColumn: 'span 8', // 40%
  },

  // Diagnosis grid (for Box 21)
  diagnosisGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 1,

    '& .MuiTextField-root': {
      '& .MuiInputBase-input': {
        textTransform: 'uppercase',
        fontFamily: 'monospace',
        fontSize: '12px',
      },
    },
  },

  // Service lines table - Enhanced with Material Design 3
  serviceLineTable: (theme: Theme) => ({
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderRadius: '4px',
    overflow: 'visible', // Changed from hidden to show border radius
    boxShadow: theme.shadows[1],
    backgroundColor: theme.palette.background.paper,

    // Ensure border radius is visible on corners
    '& thead tr:first-of-type th:first-of-type': {
      borderTopLeftRadius: '4px',
    },
    '& thead tr:first-of-type th:last-of-type': {
      borderTopRightRadius: '4px',
    },
    '& tbody tr:last-of-type td:first-of-type': {
      borderBottomLeftRadius: '4px',
    },
    '& tbody tr:last-of-type td:last-of-type': {
      borderBottomRightRadius: '4px',
    },

    '& th, & td': {
      border: '1px solid',
      borderColor: theme.palette.divider,
      padding: '6px 8px',
      fontSize: '12px',
      verticalAlign: 'middle',
    },

    '& th': {
      backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#1a1a1a',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },

    '& tbody tr': {
      transition: 'background-color 0.2s ease',

      '&:nth-of-type(even)': {
        backgroundColor:
          theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
      },

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },

    // Material Design 3 focus states for all inputs
    '& .MuiOutlinedInput-root': {
      transition: 'all 0.2s ease',

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '1px',
      },

      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },

    // Chip styling for diagnosis pointers
    '& .MuiChip-root': {
      transition: 'all 0.2s ease',

      '&:hover': {
        transform: 'scale(1.05)',
      },

      '&.MuiChip-colorPrimary': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
    },

    // Checkbox styling
    '& .MuiCheckbox-root': {
      transition: 'all 0.2s ease',

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  }),

  // Monospace fields for codes and amounts
  monospaceField: {
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: '13px',
      letterSpacing: '0.5px',
    },
  },

  // Amount fields with currency formatting
  amountField: {
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: '14px',
      fontWeight: 500,
      textAlign: 'right',
    },
  },

  // Code fields (CPT, ICD, etc)
  codeField: {
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  },

  // Primary action button style
  primaryButton: (theme: Theme) => ({
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    px: 3,
    py: 1,
    borderRadius: 1,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: theme.palette.primary.dark,
    },
  }),

  // Secondary action button style
  secondaryButton: (theme: Theme) => ({
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    px: 3,
    py: 1,
    borderRadius: 1,
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    '&:hover': {
      borderColor: theme.palette.text.secondary,
      backgroundColor: 'transparent',
    },
  }),

  // Delete button style
  deleteButton: (theme: Theme) => ({
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    px: 3,
    py: 1,
    borderRadius: 1,
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.dark,
      backgroundColor: theme.palette.error.light + '10',
    },
  }),

  actionButtons: (theme: Theme) => ({
    display: 'flex',
    gap: 2,
    flexWrap: 'wrap',
    mt: 3,
    pt: 0,

    // Desktop - space between with delete on right
    [theme.breakpoints.up('sm')]: {
      '& .MuiButton-root:last-child': {
        marginLeft: 'auto',
      },
    },

    // Mobile - all buttons stack vertically
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  }),

  // Read-only form styles - makes disabled fields look like filled-out data
  readOnlyForm: {
    // Target the exact input element with all its classes
    '& .MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
      color: '#000000', // Pure black
      WebkitTextFillColor: '#000000', // Pure black for webkit browsers
    },
    // Also target through nested selectors for broader coverage
    '& .MuiTextField-root': {
      '& .MuiInputBase-input.Mui-disabled': {
        color: '#000000', // Pure black
        WebkitTextFillColor: '#000000', // Pure black for webkit browsers
      },
      '& .MuiOutlinedInput-root.Mui-disabled': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(0, 0, 0, 0.23)', // Normal border color
        },
      },
    },
    '& .MuiRadio-root.Mui-disabled': {
      '&.Mui-checked': {
        color: 'primary.main',
      },
    },
    '& .MuiCheckbox-root.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.6)',
      '&.Mui-checked': {
        color: 'primary.main',
      },
    },
    '& .MuiChip-root.Mui-disabled': {
      opacity: 1,
    },
    // Make Select fields look normal when disabled
    '& .MuiSelect-select.Mui-disabled': {
      color: '#000000',
      WebkitTextFillColor: '#000000',
    },
  },

  // Sidebar layout styles
  sidebarLayout: (theme: Theme) => ({
    display: 'flex',
    gap: 3,
    alignItems: 'flex-start',
    position: 'relative',
    width: '100%',
    
    // Responsive: Stack vertically on smaller screens
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
  }),

  formColumn: (theme: Theme) => ({
    flex: '1 1 auto',
    minWidth: 0, // Allow shrinking
    width: '100%',
    
    [theme.breakpoints.up('lg')]: {
      width: 'calc(100% - 420px)', // Account for sidebar + gap
    },
  }),

  sidebarColumn: (theme: Theme) => ({
    position: 'sticky',
    top: 80, // Account for header
    height: 'calc(100vh - 100px)',
    width: '400px',
    flexShrink: 0,
    transition: 'all 0.3s ease',
    zIndex: 100,
    
    // Collapsed state
    '&.collapsed': {
      width: '60px',
      '& .sidebar-content': {
        opacity: 0,
        visibility: 'hidden',
      },
    },
    
    // Tablet: Narrower sidebar
    [theme.breakpoints.between('md', 'lg')]: {
      width: '350px',
    },
    
    // Mobile: Hide sidebar, show at bottom
    [theme.breakpoints.down('lg')]: {
      position: 'relative',
      top: 'auto',
      width: '100%',
      height: 'auto',
    },
  }),

  sidebarPanel: (theme: Theme) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    boxShadow: theme.shadows[3],
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    
    // Content area with scroll
    '& .sidebar-content': {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      transition: 'opacity 0.2s ease',
      
      // Custom scrollbar
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: theme.palette.action.hover,
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.action.disabled,
        borderRadius: '4px',
        '&:hover': {
          background: theme.palette.action.selected,
        },
      },
    },
    
    // Header area (always visible)
    '& .sidebar-header': {
      flexShrink: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[50],
      
      [theme.breakpoints.down('sm')]: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
      },
    },
  }),

  sidebarToggle: (theme: Theme) => ({
    position: 'absolute',
    left: -20,
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: theme.shadows[2],
    zIndex: 101,
    
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-50%) scale(1.1)',
    },
    
    // Hide on mobile
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  }),

  // Severity-based field error styles
  severityError: {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d32f2f',
        borderWidth: 2,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d32f2f',
        borderWidth: 2,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d32f2f',
        borderWidth: 2,
      },
    },
    '& .MuiFormHelperText-root': {
      color: '#d32f2f',
      fontWeight: 500,
      fontSize: '11px',
    },
  },

  severityWarning: {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ff9800',
        borderWidth: 2,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ff9800',
        borderWidth: 2,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ff9800',
        borderWidth: 2,
      },
    },
    '& .MuiFormHelperText-root': {
      color: '#ff9800',
      fontWeight: 500,
      fontSize: '11px',
    },
  },

  severityInfo: {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#0288d1',
        borderWidth: 1.5,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#0288d1',
        borderWidth: 1.5,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#0288d1',
        borderWidth: 1.5,
      },
    },
    '& .MuiFormHelperText-root': {
      color: '#0288d1',
      fontWeight: 400,
      fontSize: '11px',
    },
  },
};

// Helper function to combine multiple sx props
export const combineSx = (...sxProps: SxProps<Theme>[]): SxProps<Theme> => {
  const filtered = sxProps.filter(Boolean);
  if (filtered.length === 0) return {};
  if (filtered.length === 1) return filtered[0];
  // MUI's sx prop accepts arrays, cast it properly
  return filtered as SxProps<Theme>;
};
